import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/react';
import { useState } from 'react';
import CustomButton from '../../../components/CustomButton';
import { useAuth } from '../../../../context/AuthContext';
import { reactivateAccount } from '../../../../service/accountDeactivationService';
import { FaInfoCircle } from 'react-icons/fa';
import { FiRefreshCw } from 'react-icons/fi';
import CustomAlert from '../../../components/CustomAlert';

const ReactivateAccount = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, userId, role } = useAuth();

  const [alertMessage, setAlertMessage] = useState<{
    msg: string;
    type: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleReactivateAccount = async () => {
    try {
      setLoading(true);

      if (!userId || !user?.email || !role) {
        setAlertMessage({
          msg: 'User information is missing. Please try again later or re-login.',
          type: 'error',
        });
        return;
      }

      const payload = {
        accountType: 'customer',
        userId,
        email: user?.email,
        role: 'customer' as const,
      };

      const response = await reactivateAccount(payload);

      if (response.success) {
        setAlertMessage({
          msg: 'Your account reactivation request has been submitted successfully. Please check your email for further instructions.',
          type: 'success',
        });
        onClose();
        window.location.reload();
      } else {
        setAlertMessage({
          msg: 'Account reactivation failed. Please try again later.',
          type: 'error',
        });
      }
    } catch (error: any) {
      setAlertMessage({
        msg: 'An error occurred while processing your request. Please try again later.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between items-start gap-3 sm:mt-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <FiRefreshCw className="text-green-600" size={16} />
          </div>
          <div className="flex flex-col items-start justify-start">
            <h3 className="text-sm font-semibold text-gray-800">
              Reactivate Account
            </h3>

            <p className="text-caption">
              Restore access to your account and continue using all features and
              services.
            </p>
          </div>
        </div>

        <div className="flex justify-end w-full sm:w-auto">
          <CustomButton
            color="success"
            label="Reactivate Account"
            variant="solid"
            className="mt-1"
            onPress={() => {
              onOpen();
            }}
            startContent={<FiRefreshCw size={14} />}
          />
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
        placement="center"
        backdrop="blur"
        size="lg"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Reactivate Account
          </ModalHeader>

          <ModalBody>
            {/* Banner */}
            {alertMessage !== null && (
              <CustomAlert
                description={alertMessage.msg}
                color={alertMessage.type === 'success' ? 'success' : 'danger'}
              />
            )}

            <div className="bg-green-50 p-4 rounded border border-green-200 text-green-800 flex items-start gap-3 text-sm">
              <FaInfoCircle className="mt-0.5 text-green-600" />

              <div>
                <h4 className="font-semibold mb-2">
                  Your account is ready to be reactivated
                </h4>

                <ul className="list-none space-y-3 text-sm text-gray-700">
                  <li className="flex text-xs items-start gap-2.5">
                    <span className="text-green-500 font-bold mt-0.5">•</span>

                    <span>
                      Your account access and profile visibility will be
                      restored immediately after reactivation.
                    </span>
                  </li>

                  <li className="flex text-xs items-start gap-2.5">
                    <span className="text-green-500 font-bold mt-0.5">•</span>

                    <span>
                      Your saved settings, preferences, and account data will
                      remain available.
                    </span>
                  </li>

                  <li className="flex text-xs items-start gap-2.5">
                    <span className="text-green-500 font-bold mt-0.5">•</span>

                    <span>
                      You can continue using all platform features and services
                      as usual.
                    </span>
                  </li>

                  <li className="flex text-xs items-start gap-2.5">
                    <span className="text-green-500 font-bold mt-0.5">•</span>

                    <span>
                      Some account information may take a few moments to update
                      after reactivation.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <CustomButton
              color="default"
              variant="flat"
              label="Cancel"
              onPress={() => {
                setAlertMessage(null);
                onClose();
              }}
              isDisabled={loading}
            />

            <CustomButton
              color="success"
              variant="solid"
              label="Confirm Reactivation"
              onPress={handleReactivateAccount}
              isLoading={loading}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ReactivateAccount;
